import db from "../db/database";
import {
  fetchFromBusinessCentral,
  getCurrentEnvironment,
  parseImportResponse,
  sendToBusinessCentral,
} from "../utils/businessCentralUtils";
import { formatAxiosError } from "../utils/common";
import { formatDateForBC, parseDate } from "../utils/dateUtils";
import { getPendingSync, isOnline } from "./syncService";
import {
  conAvisoDeBajaRechazada,
  enviarLote,
  motivoDeGuardadoFallido,
  resumenDelLote,
  separarPendientes,
} from "./loteSyncHelpers";
import { anotarError, anotarFila, anotarInicioDeLote, anotarResumenDelLote } from "./syncLog";
import { countryMap } from "../constants/countries";
import { resolveMoneyDecimals, roundMoney } from "../utils/money";

// El margen de cierre es un importe: viaja redondeado a la precisión de cálculo
// del país. Sin esto la pantalla mostraba 12,35 y BC guardaba 12,345.
const margenRedondeado = (valor, countryCode) =>
  roundMoney(Number(valor) || 0, resolveMoneyDecimals(countryMap[countryCode]));

// Nombre con el que esta entidad aparece en las líneas de consola.
const ENTIDAD = "tiendas";

// Función para transformar un TPV al formato esperado por Business Central
const transformTPVForBC = (tpv, countryCode) => {
  // Transformamos el TPV al formato esperado por BC
  // Una sola expresión para las tres grafías que acepta BC. Antes eran tres
  // independientes: si alguien tocaba una y olvidaba las otras, BC se quedaba
  // con la primera no vacía sin avisar de la divergencia.
  const codigo = tpv.No || "TEMP";

  const transformedTPV = {
    // El código que se haya escrito en el formulario, o "TEMP" para que lo asigne
    // BC de la serie "Nums. TPV". TryProcesarTienda distingue los dos casos: con
    // TEMP inserta con trigger y deja que la serie ponga el código; con un código
    // propio lo respeta tal cual, que es lo que permite que la tienda nazca con su
    // código corporativo en vez de con un correlativo que luego habría que
    // reconciliar. Se manda en las tres claves porque IdTiendaSolicitada acepta
    // "No.", "No" y "No_", en ese orden.
    No_: codigo,
    No: codigo,
    "No.": codigo,
    nombre: tpv.nombre,
    direccion: tpv.direccion || "",
    direccion2: tpv.direccion2 || "",
    ciudad: tpv.ciudad || "",
    codigoPostal: tpv.codigoPostal || "",
    provincia: tpv.provincia || "",
    pais: tpv.pais || "",
    telefono: tpv.telefono || "",
    Mobil: tpv.movil || "",
    email: tpv.email || "",
    sitioWeb: tpv.sitioWeb || "",
    nifCif: tpv.nifCif || "",
    contacto: tpv.contacto || "",
    notas: tpv.notas || "",
    fechaAlta: formatDateForBC(tpv.fechaAlta) || formatDateForBC(new Date()),
    locationCode: tpv.locationCode || "",
    noSeries: tpv.noSeries || "",
    margenCierre: margenRedondeado(tpv.margenCierre, countryCode),
    Deleted: tpv.deleted,
    cod_Vendedor: tpv.cod_Vendedor || "",
  };

  // Creamos el formato final esperado por el end point
  return {
    data: JSON.stringify({ TPVs: [transformedTPV] }),
  };
};

// Payload con todas las tiendas que se manden en la misma llamada.
// Importaciones_insertaTPV recorre el array y procesa cada tienda aislada, así
// que un rechazo no arrastra a las demás.
const transformTPVsForBC = (tpvs, countryCode) => ({
  data: JSON.stringify({
    TPVs: tpvs.map((tpv) => JSON.parse(transformTPVForBC(tpv, countryCode).data).TPVs[0]),
  }),
});

// Nombre con el que se identifica la tienda en el cuadro de resultados.
const nombreDeTPV = (tpv) => tpv.nombre || tpv.No || "(sin nombre)";

/**
 * La ficha de tienda de Business Central, traducida a la fila local.
 *
 * Se exporta para poder probarlo. Es una función pura y el defecto que tenía
 * --leer una clave con otra caja de letra-- no lo caza nada más: no falla, no
 * avisa, y el campo llega vacío como si Business Central no lo tuviera.
 */
export const mapServerTPVToLocal = (serverTPV, countryCode) => {
  const mapped = {
    nombre: serverTPV.nombre,
    direccion: serverTPV.direccion ?? "",
    direccion2: serverTPV.direccion2 ?? "",
    ciudad: serverTPV.ciudad ?? "",
    codigoPostal: serverTPV.codigoPostal ?? serverTPV.codigo_postal ?? "",
    provincia: serverTPV.provincia ?? "",
    pais: serverTPV.pais ?? "",
    telefono: serverTPV.telefono ?? "",
    // `movil` en minúscula, que es como lo publica la página --el control se
    // llama así: `field(movil; Rec."Telefono 2")`--. Aquí se leía `Mobil`, con
    // mayúscula, y eso no existe en la respuesta: **el celular de la tienda no
    // ha llegado nunca**, en ningún país.
    //
    // Y no era sólo que faltara. El formulario tiene su campo «N° celular» y el
    // listado su columna, así que se podía escribir: se guardaba en local, se
    // subía bien --el importador de Business Central sí acepta la clave con
    // mayúscula, comprobado en su `TryProcesarTienda`-- y la bajada siguiente
    // lo pisaba con vacío, porque para entonces la fila ya no estaba pendiente
    // y el `update` escribe lo mapeado. Se tecleaba, parecía guardado y
    // desaparecía en la siguiente sincronización, sin un error.
    //
    // La grafía con mayúscula se conserva como respaldo, igual que en
    // `codigoPostal` y `noSeries`: no cuesta nada y cubre un ambiente que
    // publicara la variante vieja.
    movil: serverTPV.movil ?? serverTPV.Mobil ?? "",
    email: serverTPV.email ?? "",
    sitioWeb: serverTPV.sitioWeb ?? serverTPV.sitio_web ?? "",
    nifCif: serverTPV.nifCif ?? serverTPV.nif_cif ?? "",
    contacto: serverTPV.contacto ?? "",
    notas: serverTPV.notas ?? "",
    // parseDate devuelve null para el 0001-01-01 con el que BC representa "sin
    // fecha", así que ese valor no entra a la base local ni al formulario (que
    // lo mostraba como 31/12/0000 y lo reenviaba tal cual al guardar).
    fechaAlta: parseDate(serverTPV.fechaAlta || serverTPV.fecha_alta),
    locationCode: serverTPV.locationCode ?? serverTPV.location_code ?? "",
    noSeries: serverTPV.noSeries ?? serverTPV.no_series ?? "",
    margenCierre: serverTPV.margenCierre ?? 0,
    customerPriceGroup: serverTPV.CustomerPriceGroup ?? "",
    defaultCustomer: serverTPV.DefaultCustomer ?? "",
    // El colegio al que atiende esta tienda, si atiende a uno. Tercero de la
    // familia de valores por defecto de la tienda, con `DefaultCustomer` y
    // `CustomerPriceGroup`: la venta lo propone y el cajero puede cambiarlo.
    //
    // **Vacío es lo normal**, no un caso raro: la mayoría de las tiendas no
    // son de colegio, y ahí el cajero elige como hasta ahora.
    //
    // Una sola grafía y **sin respaldo alternativo**, a propósito. Los
    // respaldos de `codigoPostal`, `noSeries` y `movil` cubren grafías viejas
    // ya publicadas; este campo nace en la 1.0.4.134 y no tiene pasado que
    // cubrir. Acordado en `contrato-colegio-por-tienda`, ronda 4, y no se
    // toca: dos grafías que difieran en un guion bajo dejan el campo vacío en
    // silencio, que es exactamente lo que pasó con el celular (caso 162).
    cod_Colegio: serverTPV.cod_Colegio ?? "",
    cod_Vendedor: serverTPV.cod_Vendedor ?? "",
    // Centro de responsabilidad de la tienda en BC. Sólo se baja para mostrarlo
    // en Mi perfil; no se sube en `transformTPVsForBC`.
    responsibilityCenter: serverTPV.Responsibility_Center ?? "",
    lastModified: new Date(),
    lastSynced: new Date(),
  };

  return mapped;
};

// BC rechazó la baja: se revierte el borrado local para que la tienda vuelva a
// aparecer en el listado y local y BC no queden divergentes. Mismo criterio que
// en userSyncService y turnoSyncService.
const restoreDeletedTPV = async (tpv, reason) => {
  await db.tpvs.update(tpv.id, {
    deleted: false,
    pendingSync: 0,
    lastModified: new Date(),
    // El motivo que devolvió BC se guarda en el propio registro. Es lo único
    // que sobrevive a la corrida: el registro reaparece en el listado con
    // pendingSync 0, o sea con aspecto de estar perfectamente sincronizado, y
    // sin esto no queda nadie que pueda explicar por qué volvió. Lo lee
    // RecordSyncTag. Ver SANTPV-59.
    syncError: reason || "Business Central no aplicó la baja.",
  });
};

// Sincroniza una sola tienda contra BC, para el botón de sincronizar de cada
// fila del listado. Devuelve { ok, message } para poder mostrarle el resultado
// a quien apretó el botón, en vez de acumularlo en un resumen.
export const syncTPVById = async (tpvId) => {
  if (!isOnline()) {
    return { ok: false, message: "No hay conexión a internet" };
  }

  try {
    const tpv = await db.tpvs.get(tpvId);
    if (!tpv) {
      return { ok: false, message: "Tienda no encontrada" };
    }

    const env = await getCurrentEnvironment();
    const countryCode = env?.country;

    if (tpv.deleted) {
      // Nunca llegó a BC: sólo existe en local.
      if (!tpv.No) {
        await db.tpvs.delete(tpv.id);
        return { ok: true, message: "Tienda eliminada" };
      }

      const response = await sendToBusinessCentral(
        "Importaciones_insertaTPV",
        transformTPVForBC(tpv, countryCode),
        true
      );
      const result = parseImportResponse(response, tpv.No);

      if (!result.ok) {
        await restoreDeletedTPV(tpv, result.message);
        return {
          ok: false,
          message:
            result.message || "Business Central no aplicó la baja. La tienda sigue en el listado.",
        };
      }

      await db.tpvs.delete(tpv.id);
      return { ok: true, message: "Tienda dada de baja en Business Central" };
    }

    const response = await sendToBusinessCentral(
      "Importaciones_insertaTPV",
      transformTPVForBC(tpv, countryCode),
      true
    );
    const result = parseImportResponse(response, tpv.No || "TEMP");

    if (!result.ok || !result.assignedNo) {
      return {
        ok: false,
        message: result.message || "Respuesta inválida de Business Central",
      };
    }

    // Se lee antes del update, que es justo lo que lo borra.
    const teniaBajaRechazada = Boolean(tpv.syncError);

    await db.tpvs.update(tpv.id, {
      No: result.assignedNo,
      lastModified: new Date(),
      lastSynced: new Date(),
      pendingSync: 0,
      syncError: null,
    });

    return {
      ok: true,
      message: conAvisoDeBajaRechazada("Tienda sincronizada correctamente.", teniaBajaRechazada),
    };
  } catch (error) {
    anotarError(ENTIDAD, `falló el envío del registro ${tpvId}`, error);
    return {
      ok: false,
      message: formatAxiosError(error, "No se pudo sincronizar la tienda"),
    };
  }
};

export const syncTPVs = async () => {
  if (!isOnline()) {
    return false;
  }

  // Tiendas cuyo push a BC falló (o fue ignorado por BC) en esta corrida.
  // Cada entrada es { tpv, action: "delete" | "save", reason }. Antes los
  // errores sólo se escribían en consola y la función devolvía true igual, así
  // que un fallo de BC pasaba por sincronización completada.
  const failedTPVs = [];

  // Una fila por registro enviado, para el cuadro que se le muestra a quien
  // apretó Sincronizar. Incluye los correctos, no sólo los fallos.
  const detalle = [];

  try {
    const env = await getCurrentEnvironment();
    const countryCode = env?.country;
    const pendingTPVs = await getPendingSync("tpvs");

    if (pendingTPVs.length > 0) {
      const { soloLocales, aEnviar } = separarPendientes(pendingTPVs);

      for (const tpv of soloLocales) {
        await db.tpvs.delete(tpv.id);
        detalle.push(
          anotarFila(
            ENTIDAD,
            {
              registro: nombreDeTPV(tpv),
              no: null,
              accion: "deleted",
              ok: true,
              mensaje: "Eliminado sólo en local: nunca llegó a Business Central",
            },
            tpv.id,
            "baja-local"
          )
        );
      }

      if (aEnviar.length > 0) {
        anotarInicioDeLote(ENTIDAD, aEnviar.length);

        // UNA llamada para todos los pendientes, en vez de una por registro.
        const lote = await enviarLote(
          "Importaciones_insertaTPV",
          transformTPVsForBC(aEnviar, countryCode),
          aEnviar.length,
          "tiendas"
        );

        // El cruce es por posición: BC devuelve los resultados en el mismo orden
        // en que recibió los registros. Por No_ no se podría, porque todas las
        // altas se mandan como "TEMP" y volverían indistinguibles entre sí.
        for (let indice = 0; indice < aEnviar.length; indice++) {
          const tpv = aEnviar[indice];
          const resultado = lote.results[indice];
          let traiaBajaRechazada = false;

          if (tpv.deleted) {
            if (resultado.ok) {
              await db.tpvs.delete(tpv.id);
            } else {
              // BC no aplicó la baja: el registro NO se borra en local, se
              // revierte el borrado y el motivo queda guardado en él.
              await restoreDeletedTPV(tpv, resultado.message);
              failedTPVs.push({ tpv, action: "delete", reason: resultado.message });
            }
          } else if (resultado.ok && resultado.assignedNo) {
            // Se anota antes del update, que es lo que borra el motivo anterior.
            traiaBajaRechazada = Boolean(tpv.syncError);

            await db.tpvs.update(tpv.id, {
              No: resultado.assignedNo,
              lastModified: new Date(),
              lastSynced: new Date(),
              pendingSync: 0,
              syncError: null,
            });
          } else {
            const motivo = motivoDeGuardadoFallido(resultado);
            failedTPVs.push({ tpv, action: "save", reason: motivo });
          }

          detalle.push(
            anotarFila(
              ENTIDAD,
              {
                registro: nombreDeTPV(tpv),
                no: resultado.assignedNo || tpv.No || null,
                accion: resultado.action || (tpv.deleted ? "deleted" : "saved"),
                ok: tpv.deleted ? resultado.ok : resultado.ok && Boolean(resultado.assignedNo),
                mensaje: conAvisoDeBajaRechazada(resultado.message || "", traiaBajaRechazada),
              },
              tpv.id,
              tpv.deleted ? "baja" : undefined
            )
          );
        }
      }
    }

    // Obtener tiendas del servidor después de sincronizar las pendientes
    const serverTPVs = await fetchFromBusinessCentral("ListaTPV");
    const localTPVs = await db.tpvs.toArray();

    // Mapa para buscar TPVs locales por No rápidamente
    const localTPVsByNo = {};
    localTPVs.forEach((tpv) => {
      if (tpv.No) {
        localTPVsByNo[tpv.No] = tpv;
      }
    });

    // Actualizar TPVs existentes y añadir nuevos desde el servidor
    for (const serverTPV of serverTPVs) {
      const localTPV = localTPVsByNo[serverTPV.No];
      const mappedTPV = mapServerTPVToLocal(serverTPV, countryCode);

      if (localTPV) {
        if (localTPV.pendingSync === 0) {
          await db.tpvs.update(localTPV.id, mappedTPV);
        }
      } else {
        await db.tpvs.add({
          No: serverTPV.No,
          ...mappedTPV,
          pendingSync: 0,
          deleted: false,
        });
      }
    }

    // Cierra el lote en consola con el mismo recuento que ve quien apretó

    // Sincronizar. Antes había que sumar las líneas a ojo.

    const resumen = resumenDelLote(detalle);

    anotarResumenDelLote(ENTIDAD, resumen);

    if (failedTPVs.length > 0) {
      const deletes = failedTPVs.filter((f) => f.action === "delete");
      const saves = failedTPVs.filter((f) => f.action === "save");

      const parts = [];
      if (deletes.length > 0) {
        parts.push(
          `${deletes.length} tienda(s) no se pudieron eliminar en Business Central y siguen en el listado`
        );
      }
      if (saves.length > 0) {
        parts.push(`${saves.length} tienda(s) no se pudieron guardar en Business Central`);
      }

      // Motivo concreto que devolvió BC, si lo hubo.
      const reason = failedTPVs.find((f) => f.reason)?.reason;

      return {
        success: false,
        failedCount: failedTPVs.length,
        message: reason ? `${parts.join(". ")}: ${reason}` : parts.join(". "),
        lote: resumen,
      };
    }

    // Se devuelve el detalle también cuando todo salió bien: la pantalla muestra
    // el mismo cuadro en los dos casos, y `executeWithRetry` sólo mira
    // `success === false`, así que un objeto acá no cambia nada para syncAll.
    return { success: true, lote: resumen };
  } catch (error) {
    anotarError(ENTIDAD, "falló la sincronización", error);
    return false;
  }
};
